import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

export interface InternalApiKeyTokenBannerProps {
  token: string;
  kind: "created" | "rotated";
  keyName: string;
  onDismiss: () => void;
}

/**
 * On the page, not in the drawer that produced it: an overlay closes on Escape and the token cannot be read
 * again.
 */
export function InternalApiKeyTokenBanner({
  token,
  kind,
  keyName,
  onDismiss,
}: InternalApiKeyTokenBannerProps) {
  const { t } = useTranslations("internalApiKeys");
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(token)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <ColocaAlert variant="warning">
      <p className="text-sm font-medium text-foreground">
        {kind === "created"
          ? t("token_title_created", { name: keyName })
          : t("token_title_rotated", { name: keyName })}
      </p>
      <p className="text-[12px] text-muted-foreground">{t("token_warning")}</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded bg-muted px-2.5 py-2 font-mono text-[12px] text-foreground">
          {token}
        </code>
        <ColocaButton variant="default" size="xs" icon={copied ? Check : Copy} onClick={copy}>
          {copied ? t("token_copied") : t("token_copy")}
        </ColocaButton>
      </div>
      <ColocaButton variant="ghost" size="xs" className="mt-2" onClick={onDismiss}>
        {t("token_dismiss")}
      </ColocaButton>
    </ColocaAlert>
  );
}
